// Tipos e regras compartilhadas da criação de campanhas (fatia S5a): rascunhos, validação, publicação e criativos.
// Valores em dinheiro em REAIS. Datas 'YYYY-MM-DD'.
import type { Goal } from '../domain'

/** Plataformas onde o assistente cria campanhas (a conexão "fake" ocupa a vaga de qualquer uma em demonstração). */
export type BuilderPlatform = 'meta' | 'google_ads' | 'tiktok' | 'openai_ads'
export const BUILDER_PLATFORMS: BuilderPlatform[] = ['meta', 'google_ads', 'tiktok', 'openai_ads']
export const PLATFORM_LABEL: Record<BuilderPlatform, string> = { meta: 'Instagram/Facebook', google_ads: 'Google', tiktok: 'TikTok', openai_ads: 'ChatGPT' }

export type BudgetSplit = 'auto' | 'meta' | 'google' | 'tiktok' | 'openai'
export type DraftSource = 'user' | 'autopilot' | 'idea' | 'assistant'
export type DraftStatus = 'draft' | 'publishing' | 'published' | 'failed'

/** Limites de texto e orçamento usados na validação (e nos contadores do front). */
export const BUILDER_LIMITS = {
  metaHeadlineMax: 40,
  metaBodyRecommended: 125,
  googleHeadlineMax: 30,
  googleDescriptionMax: 90,
  minDailyPerPlatform: 5, // R$
  tiktokMinDaily: 100, // R$ por grupo de anúncios (igual a platformMinBudgetMicros('tiktok', 'ad_group') em decisions/rules.ts)
  tiktokBodyMin: 12,
  tiktokBodyMax: 100,
  openaiTitleMin: 3, // cartão de conversa do ChatGPT
  openaiTitleMax: 50,
  openaiBodyMax: 100,
  openaiImageMin: 640, // px, menor lado
  ageMin: 18,
  ageMax: 65,
  radiusMin: 1,
  radiusMax: 80,
  autoMetaShare: 0.7,
} as const

/** Termos que costumam fazer anúncios serem barrados (saúde, promessas, temas restritos). Comparação sem acento, por palavra. */
export const POLICY_RISK_TERMS: string[] = [
  'cura', 'curar', 'milagre', 'milagroso', 'milagrosa', 'garantido', 'garantida', 'resultado garantido', '100% garantido',
  'emagreca', 'emagrecer', 'emagrecimento rapido', 'perca peso', 'perder peso', 'queime gordura', 'antes e depois',
  'dinheiro facil', 'fique rico', 'enriqueca', 'renda extra garantida', 'sem riscos', 'risco zero',
  'aposta', 'apostas', 'cassino', 'bet', 'arma', 'armas', 'cigarro', 'vape', 'cannabis', 'maconha',
  'remedio', 'receita medica', 'tratamento definitivo', 'voce esta acima do peso', 'voce tem divida', 'gratis para sempre',
]

/** Ramos que vendem pela internet: o anúncio vai para o Brasil todo em vez de um raio em volta da loja. */
export const ONLINE_SEGMENTS = ['Software / SaaS', 'Loja virtual', 'Curso ou infoproduto', 'Serviços para empresas']

/** Público padrão no Brasil todo: negócio online, objetivo de site ou cadastros, ou empresa sem cidade. */
export const defaultNationwide = (c: { segment?: string | null; goal?: string | null; city?: string | null }) =>
  ONLINE_SEGMENTS.includes(c.segment ?? '') || c.goal === 'site' || c.goal === 'leads' || !c.city?.trim()

export interface BlueprintAudience {
  radiusKm: number
  /** true = Brasil todo (ignora o raio e a cidade) */
  nationwide?: boolean
  ageMin: number
  ageMax: number
  interests: string[]
  advantageAudience: boolean
  keywords?: string[]
}

export interface BlueprintCreative {
  headline: string
  body: string
  imageAssetId?: string | null
  alternatives?: Array<{ headline: string; body: string }>
  /** Instagram/Facebook: post já publicado usado como criativo (o anúncio leva o texto e a imagem dele) */
  metaPost?: MetaPostRef | null
}

/** Post da Página do Facebook ou do perfil do Instagram. text/imageUrl/permalink servem só para mostrar na tela. */
export interface MetaPostRef {
  source: 'facebook' | 'instagram'
  id: string
  text?: string | null
  imageUrl?: string | null
  permalink?: string | null
}

/** GET /companies/:companyId/builder/meta-posts */
export interface MetaPostsResponse {
  items: Array<MetaPostRef & { createdAt: string | null }>
  /** Avisos (ex.: Instagram sem permissão); os posts que deu para ler vêm mesmo assim */
  notes: string[]
}

export interface BlueprintBudget {
  dailyBudget: number
  startDate?: string | null
  endDate?: string | null
  split: BudgetSplit
}

/** Para onde o anúncio leva (conforme o objetivo). */
export interface BlueprintDestination {
  whatsappNumber?: string | null
  url?: string | null
  phone?: string | null
}

export interface BlueprintDto {
  goal: Goal
  name: string
  offer: string
  audience: BlueprintAudience
  creative: BlueprintCreative
  budget: BlueprintBudget
  destination?: BlueprintDestination
  connections?: Partial<Record<'google_ads' | 'meta' | 'tiktok' | 'openai_ads' | 'fake', string>>
}

/** Blueprint parcial (POST/PATCH): cada seção é mesclada com o que já existe. */
export interface BlueprintPatch {
  goal?: Goal
  name?: string
  offer?: string
  audience?: Partial<BlueprintAudience>
  creative?: Partial<BlueprintCreative>
  budget?: Partial<BlueprintBudget>
  destination?: Partial<BlueprintDestination>
}

export type PlatformPublishStatus = 'pending' | 'creating' | 'created' | 'activating' | 'active' | 'failed'

export interface PlatformPublishState {
  platform: BuilderPlatform
  connectionId: string
  dailyBudget: number
  status: PlatformPublishStatus
  createActionId?: string | null
  activateActionId?: string | null
  campaignExternalId?: string | null
  error?: string | null
}

export interface PublishResultDto {
  attempt: number
  phase: 'queued' | 'creating' | 'activating' | 'done' | 'failed'
  requestedByUserId: string | null
  startedAt: string
  finishedAt?: string | null
  platforms: Partial<Record<BuilderPlatform, PlatformPublishState>>
  error?: string | null
}

export interface DraftDto {
  id: string
  companyId: string
  name: string
  source: DraftSource
  status: DraftStatus
  blueprint: BlueprintDto
  publishResult: PublishResultDto | null
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
}

/** GET /companies/:companyId/drafts */
export interface DraftListResponse { items: DraftDto[] }
/** POST /companies/:companyId/drafts  { blueprint?: BlueprintPatch } */
export interface CreateDraftBody { blueprint?: BlueprintPatch }
/** PATCH /companies/:companyId/drafts/:id */
export interface PatchDraftBody { blueprint: BlueprintPatch }

export interface ValidationCheck {
  id: string
  ok: boolean
  message: string
  severity: 'error' | 'warning' | 'info'
}

/** POST /companies/:companyId/drafts/:id/validate */
export interface ValidateResponse {
  ok: boolean
  checks: ValidationCheck[]
  platforms: BuilderPlatform[]
  split: Partial<Record<BuilderPlatform, number>>
}

/** GET /companies/:companyId/drafts/:id/estimate */
export interface EstimateResponse {
  low: number
  high: number
  costPerResultLow: number | null
  costPerResultHigh: number | null
  basis: 'history' | 'benchmark' | 'none'
  days: number
  dailyBudget: number
  total: number
}

export interface BuilderPlatformOption {
  platform: BuilderPlatform
  connected: boolean
  usable: boolean
  connectionId: string | null
  accountName: string | null
  status: string | null
  demo: boolean
  problem: string | null
}

/** GET /companies/:companyId/builder/options?renovar=<adId> */
export interface BuilderOptionsResponse {
  companyName: string
  city: string | null
  segment: string | null
  goal: Goal | null
  website: string | null
  platforms: BuilderPlatformOption[]
  renew: { adId: string; name: string; headline: string | null; body: string | null } | null
}

export interface AssetDto {
  id: string
  kind: 'image' | 'video' | 'logo' | 'report'
  mime: string
  sizeBytes: number
  width: number | null
  height: number | null
  originalName: string | null
  url: string
  createdAt: string
}

/** GET /companies/:companyId/assets */
export interface AssetListResponse { items: AssetDto[] }

export const ASSET_MAX_BYTES = 15 * 1024 * 1024
export const ASSET_ACCEPT = 'image/jpeg,image/png,image/webp'

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * Divisão do orçamento diário entre plataformas.
 * auto: 70% Meta / 30% Google quando as duas estão disponíveis; senão 100% na disponível.
 * 'meta'/'google'/'tiktok'/'openai': 100% na escolhida (se disponível).
 * O TikTok só entra no auto se for a única conexão (orçamento mínimo alto e anúncio de imagem só na rede parceira); o ChatGPT também (formato novo, só tráfego).
 */
export function splitBudget(dailyBudget: number, split: BudgetSplit, available: Partial<Record<BuilderPlatform, boolean>>): Partial<Record<BuilderPlatform, number>> {
  const meta = !!available.meta
  const google = !!available.google_ads
  const tiktok = !!available.tiktok
  const openai = !!available.openai_ads
  if (split === 'meta') return meta ? { meta: round2(dailyBudget) } : {}
  if (split === 'google') return google ? { google_ads: round2(dailyBudget) } : {}
  if (split === 'tiktok') return tiktok ? { tiktok: round2(dailyBudget) } : {}
  if (split === 'openai') return openai ? { openai_ads: round2(dailyBudget) } : {}
  if (meta && google) {
    const m = round2(dailyBudget * BUILDER_LIMITS.autoMetaShare)
    return { meta: m, google_ads: round2(dailyBudget - m) }
  }
  if (meta) return { meta: round2(dailyBudget) }
  if (google) return { google_ads: round2(dailyBudget) }
  if (tiktok) return { tiktok: round2(dailyBudget) }
  if (openai) return { openai_ads: round2(dailyBudget) }
  return {}
}

/** Remove acentos e baixa a caixa (para comparar termos). */
export function foldText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** Termos de risco encontrados no texto (por palavra inteira, sem acento). */
export function findPolicyTerms(text: string): string[] {
  const t = ` ${foldText(text).replace(/[^a-z0-9%]+/g, ' ')} `
  return POLICY_RISK_TERMS.filter((term) => t.includes(` ${term} `))
}

export const GOAL_LABEL: Record<Goal, string> = {
  whatsapp: 'Receber mensagens no WhatsApp',
  site: 'Vender no site ou loja online',
  leads: 'Conseguir cadastros no site',
  store: 'Trazer gente até a loja',
  calls: 'Receber ligações',
}
